import {isNotNil} from 'ramda';

export interface AccountingDoorbellTarget {
  checkoutId: string;
  orderId: string;
  paymentId: string;
}

interface CachedQueryEntry {
  endpointName?: string;
  originalArgs?: unknown;
  data?: unknown;
}

const GET_ORDER_ENDPOINT = 'getOrder';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  isNotNil(value) && typeof value === 'object';

const readString = (source: Record<string, unknown>, key: string) => {
  const value = source[key];

  return typeof value === 'string' ? value : null;
};

const readFirstPaymentId = (data: unknown) => {
  if (!isRecord(data) || !Array.isArray(data.payments)) {
    return null;
  }

  const firstPayment: unknown = data.payments[0];

  return isRecord(firstPayment) ? readString(firstPayment, 'id') : null;
};

/**
 * Picks the checkout order currently held in the RTK Query cache, so the doorbell
 * harness can be fired without hand-copying three UUIDs out of the Network tab.
 *
 * Reads the cache rather than the URL because the checkout id never appears in
 * the route, and the payment id exists only inside the order response.
 */
export const getAccountingDoorbellTarget = (
  queries: Record<string, CachedQueryEntry | undefined> | undefined
): AccountingDoorbellTarget | null => {
  const entries = Object.values(queries ?? {});

  for (const entry of entries) {
    if (entry?.endpointName !== GET_ORDER_ENDPOINT || !isRecord(entry.originalArgs)) {
      continue;
    }

    const checkoutId = readString(entry.originalArgs, 'checkoutId');
    const orderId = readString(entry.originalArgs, 'orderId');
    const paymentId = readFirstPaymentId(entry.data);

    if (isNotNil(checkoutId) && isNotNil(orderId) && isNotNil(paymentId)) {
      return {checkoutId, orderId, paymentId};
    }
  }

  return null;
};
